/**
 * Mucka's reply text, markdown → HTML: **bold**, tables, bullet and numbered
 * lists, paragraphs and line breaks. Escapes HTML first, so nothing she writes
 * is ever markup.
 *
 * Shared by the desktop panel and the phone's chat bar (MuckaReplyText). It
 * lived inside MuckaPanel, so the bar printed the raw text and every phone got
 * one unbroken block with the asterisks in it (26 Oct 2026). Pure and
 * directive-free (anti-pattern #18).
 */
export function renderMarkdown(text: string): string {
  // Escape HTML entities first
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Check for markdown table
  if (html.includes('|') && html.includes('---')) {
    html = html.replace(
      /(?:^|\n)((?:\|[^\n]+\|\n?)+)/g,
      (_match, tableBlock: string) => {
        const rows = tableBlock.trim().split('\n').filter((r: string) => r.trim());
        // Filter out separator rows (|---|---|)
        const dataRows = rows.filter((r: string) => !/^\|[\s\-:|]+\|$/.test(r.trim()));
        if (dataRows.length === 0) return tableBlock;

        const headerRow = dataRows[0];
        const bodyRows = dataRows.slice(1);

        const parseRow = (row: string) =>
          row.split('|').filter((c: string) => c.trim() !== '').map((c: string) => c.trim());

        let tableHtml = '<table class="mp-table"><thead><tr>';
        for (const cell of parseRow(headerRow)) {
          tableHtml += `<th>${cell}</th>`;
        }
        tableHtml += '</tr></thead><tbody>';
        for (const row of bodyRows) {
          tableHtml += '<tr>';
          for (const cell of parseRow(row)) {
            tableHtml += `<td>${cell}</td>`;
          }
          tableHtml += '</tr>';
        }
        tableHtml += '</tbody></table>';
        return tableHtml;
      }
    );
  }

  // Bold **text**
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Bullet lists (lines starting with - or *)
  html = html.replace(
    /(?:^|\n)((?:[\-\*]\s+[^\n]+\n?)+)/g,
    (_match, listBlock: string) => {
      const items = listBlock.trim().split('\n')
        .map((l: string) => l.replace(/^[\-\*]\s+/, '').trim())
        .filter(Boolean);
      return '<ul class="mp-list">' + items.map((item: string) => `<li>${item}</li>`).join('') + '</ul>';
    }
  );

  // Numbered lists (lines starting with 1. 2. etc)
  html = html.replace(
    /(?:^|\n)((?:\d+\.\s+[^\n]+\n?)+)/g,
    (_match, listBlock: string) => {
      const items = listBlock.trim().split('\n')
        .map((l: string) => l.replace(/^\d+\.\s+/, '').trim())
        .filter(Boolean);
      return '<ol class="mp-list">' + items.map((item: string) => `<li>${item}</li>`).join('') + '</ol>';
    }
  );

  // Line breaks (double newline → paragraph break, single → <br>)
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');

  // Wrap in paragraph if not already wrapped
  if (!html.startsWith('<')) {
    html = `<p>${html}</p>`;
  }

  return html;
}